import { useState, useEffect } from 'react';
import { storesAPI } from '../services/api';
import { notify } from '../utils/notify';
import { HiShoppingBag, HiPlus, HiRefresh, HiLogout, HiExternalLink } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../components/ConfirmModal';
import ModalShell from '../components/ModalShell';
import ComisionDeLaTienda from '../components/ComisionDeLaTienda';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import { mensajeDeError } from '../utils/apiError';


export default function Stores() {
    const [stores, setStores] = useState([]);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const [storeToDelete, setStoreToDelete] = useState(null);
    const [shopModalOpen, setShopModalOpen] = useState(false);
    const [shopDomain, setShopDomain] = useState('');
    const [conectandoShopify, setConectandoShopify] = useState(false);
    const { t } = useTranslation();


    useEffect(() => {
        fetchStores();
    }, []);

    // La vuelta del callback de Shopify aterriza aquí con `?connected=shopify`
    // (o `?error=...`). Se avisa con un modal —no un mensaje que se desvanece—
    // y se limpia la dirección para que recargar no repita el aviso.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('connected') === 'shopify') {
            notify.success(t('stores.shopify_connected'));
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('error')) {
            notify.error(t('stores.shopify_connect_error'));
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [t]);

    const fetchStores = () =>
        ejecutar(async () => {
            const response = await storesAPI.getAll();
            setStores(response.data.data.stores || []);
        });

    // eBay no admite pegar credenciales a mano: hay que pasar por su pantalla de
    // permisos para obtener un token que permita publicar.
    const conectarEbay = async () => {
        try {
            const respuesta = await storesAPI.ebayAuthUrl();
            window.location.href = respuesta.data.data.url;
        } catch (error) {
            notify.error(mensajeDeError(error, t('stores.ebay_connect_failed')));
        }
    };

    // Las apps del Dev Dashboard nuevo de Shopify ya no admiten el OAuth clásico
    // ni el token `shpat_` a mano: el backend obtiene el token con el
    // `client_credentials` de la app, así que aquí solo hace falta el dominio.
    // Acepta un dominio para prerrellenarlo: al «Reconectar» una tienda concreta,
    // el modal se abre con su dominio ya puesto.
    const abrirShopify = (dominio = '') => {
        setShopDomain(typeof dominio === 'string' ? dominio : '');
        setShopModalOpen(true);
    };

    // Deja solo el dominio `x.myshopify.com`: quita el protocolo, cualquier ruta
    // y mayúsculas, para que dé igual cómo lo pegue la persona.
    const normalizarDominioShopify = (valor) =>
        valor.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase();

    const conectarShopify = async () => {
        const dominio = normalizarDominioShopify(shopDomain);
        if (!dominio || conectandoShopify) return;
        setConectandoShopify(true);
        try {
            // El backend obtiene el token con el `client_credentials` de la app y,
            // si funciona, guarda o reactiva la tienda. Aquí solo mandamos el
            // dominio.
            await storesAPI.shopifyConnect({ shop: dominio });
            notify.success(t('stores.shopify_connected'));
            setShopModalOpen(false);
            setShopDomain('');
            fetchStores();
        } catch (error) {
            notify.error(mensajeDeError(error, t('stores.shopify_connect_failed')));
        } finally {
            setConectandoShopify(false);
        }
    };

    const handleDeleteClick = (storeId) => {
        setStoreToDelete(storeId);
    };

    const confirmDelete = async () => {
        if (!storeToDelete) return;
        try {
            const res = await storesAPI.delete(storeToDelete);
            const { keptProducts = 0, keptOrders = 0 } = res.data?.data || {};
            notify.success(
                keptProducts + keptOrders > 0
                    ? t('stores.disconnect_success_kept', { products: keptProducts, orders: keptOrders })
                    : t('stores.disconnect_success')
            );
            fetchStores();
        } catch (error) {
            notify.error(t('stores.disconnect_failed'));
        } finally {
            setStoreToDelete(null);
        }
    };

    const platformIcons = {
        shopify: '🛍️',
        woocommerce: '🔧',
        ebay: '📦'
    };

    // eBay admite una sola tienda por usuario. Si ya hay una activa, «Conectar
    // con eBay» no debe poder pulsarse: su opuesto, «Desconectar», vive en la
    // tarjeta, y dos botones que hacen lo contrario no pueden estar activos a la
    // vez. Cuando la tienda está inactiva, la tarjeta ofrece «Reconectar».
    const tiendaEbayActiva = stores.some((s) => s.platform === 'ebay' && s.is_active);

    // Reconectar depende de la plataforma: eBay pasa por su OAuth; Shopify pide
    // primero el dominio de la tienda.
    const reconectar = (store) =>
        store.platform === 'ebay' ? conectarEbay() : abrirShopify(store.store_name);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <HiShoppingBag className="w-8 h-8 text-accent-500" />
                        {t('stores.title')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('stores.subtitle')}
                    </p>
                </div>

                <div className="flex flex-col sm:items-end gap-2">
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <button
                        onClick={conectarEbay}
                        disabled={tiendaEbayActiva}
                        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                        title={tiendaEbayActiva ? t('stores.ebay_already_connected') : t('stores.connect_ebay_hint')}
                    >
                        <HiShoppingBag className="w-5 h-5 mr-2" />
                        {t('stores.connect_ebay')}
                    </button>
                    {/* Shopify ya se puede conectar de verdad (fase 3): OAuth
                        desde el dominio de la tienda, sin pegar claves a mano. */}
                    <button onClick={() => abrirShopify()} className="btn-secondary" title={t('stores.connect_shopify_hint')}>
                        <HiPlus className="w-5 h-5 mr-2" />
                        {t('stores.connect_shopify')}
                    </button>
                    </div>

                    {/* Por qué «Conectar con eBay» está apagado, escrito junto
                        al botón: un `title` solo aparece al pasar el ratón, y en
                        un móvil no aparece nunca. */}
                    {tiendaEbayActiva && (
                        <p className="text-xs text-slate-500 max-w-md sm:text-right">
                            {t('stores.ebay_already_connected')}
                        </p>
                    )}

                    {/* WooCommerce sigue sin estar; se dice, pegado a los
                        botones, para que no se dé por hecho que también va. */}
                    <p className="text-xs text-slate-500 max-w-md sm:text-right">
                        {t('stores.woocommerce_unavailable')}
                    </p>
                </div>
            </div>

            {/* Stores Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-48 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
                    ))}
                </div>
            ) : error ? (
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            ) : stores.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {stores.map((store) => (
                        <div key={store.id} className="card-hover">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">{platformIcons[store.platform]}</span>
                                    <div>
                                        <h3 className="font-semibold text-slate-900 dark:text-white">
                                            {store.store_name}
                                        </h3>
                                        <p className="text-sm text-slate-500 capitalize">{store.platform}</p>
                                    </div>
                                </div>
                                <span className={`badge ${store.is_active ? 'badge-success' : 'badge-danger'}`}>
                                    {store.is_active ? t('stores.status_active') : t('stores.status_inactive')}
                                </span>
                            </div>

                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <a
                                    href={store.store_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary-500 hover:underline flex items-center gap-1"
                                >
                                    {store.store_url}
                                    <HiExternalLink className="w-3 h-3" />
                                </a>
                                {store.last_sync_at && (
                                    <p className="text-xs text-slate-500 mt-2">
                                        {t('stores.last_synced')}: {new Date(store.last_sync_at).toLocaleString()}
                                    </p>
                                )}
                                {/* Lo que se lleva este destino de cada venta. Entra
                                    en la ganancia de todos sus productos. */}
                                <ComisionDeLaTienda
                                    store={store}
                                    onGuardada={(actualizada) =>
                                        setStores((actuales) =>
                                            actuales.map((s) => (s.id === actualizada.id ? actualizada : s))
                                        )
                                    }
                                />
                            </div>

                            <div className="flex gap-2 mt-4">
                                {/* Apagado de verdad, no solo de aspecto.
                                    Llevaba `opacity-60 cursor-not-allowed` —que
                                    es apariencia— y ningún `disabled`: se podía
                                    pulsar, se podía enfocar con el teclado, y un
                                    lector de pantalla lo anunciaba como un botón
                                    activo. El aviso de que no está disponible
                                    llegaba **después** de pulsarlo.

                                    El hermano de esta misma pantalla ya estaba
                                    bien hecho: `disabled` más explicación al
                                    lado. */}
                                <button
                                    type="button"
                                    disabled
                                    className="btn-secondary btn-sm flex-1 opacity-60 cursor-not-allowed"
                                    title={t('stores.sync_unavailable')}
                                >
                                    <HiRefresh className="w-4 h-4 mr-1" />
                                    {t('stores.sync')}
                                </button>
                                {/* Era un icono de papelera roja sin texto, y su
                                    única etiqueta era un tooltip con una clave de
                                    idioma que no existía: al pasar el ratón salía
                                    literalmente «stores.disconnect». Nadie podía
                                    encontrar el botón del que hablaba la interfaz.

                                    Y desde que desconectar ya no borra nada, una
                                    papelera roja anunciaba una destrucción que no
                                    ocurre. Ahora lleva su palabra escrita, que es
                                    lo que se busca. */}
                                {/* Desconectar y Reconectar son opuestos: nunca
                                    los dos a la vez. Con la tienda activa se
                                    ofrece soltar el cable; con la tienda ya
                                    desconectada, volver a conectarla. */}
                                {store.is_active ? (
                                    <button
                                        onClick={() => handleDeleteClick(store.id)}
                                        className="btn-secondary btn-sm"
                                    >
                                        <HiLogout className="w-4 h-4 mr-1" />
                                        {t('stores.disconnect')}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => reconectar(store)}
                                        className="btn-primary btn-sm"
                                    >
                                        <HiRefresh className="w-4 h-4 mr-1" />
                                        {t('stores.reconnect')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t('stores.no_stores_title')}</h3>
                    <p className="text-slate-500 max-w-md mx-auto mb-6">
                        {t('stores.no_stores_desc')}
                    </p>
                    <button onClick={conectarEbay} className="btn-primary">
                        <HiShoppingBag className="w-5 h-5 mr-2" />
                        {t('stores.connect_ebay')}
                    </button>
                </div>
            )}

            <ConfirmModal
                isOpen={!!storeToDelete}
                onClose={() => setStoreToDelete(null)}
                onConfirm={confirmDelete}
                title={t('common.confirm_title', 'Confirmar acción')}
                message={t('stores.disconnect_confirm')}
                confirmText={t('common.disconnect', 'Desconectar')}
            />

            {shopModalOpen && (
                <ModalShell
                    title={t('stores.shopify_modal_title')}
                    onClose={() => setShopModalOpen(false)}
                    footer={
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={conectarShopify}
                                disabled={conectandoShopify || !shopDomain.trim()}
                                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {conectandoShopify ? t('stores.shopify_connecting') : t('stores.shopify_connect')}
                            </button>
                            <button type="button" onClick={() => setShopModalOpen(false)} className="btn-secondary flex-1">
                                {t('common.cancel')}
                            </button>
                        </div>
                    }
                >
                    <label htmlFor="shop-domain" className="label">{t('stores.shopify_domain_label')}</label>
                    <input
                        id="shop-domain"
                        type="text"
                        value={shopDomain}
                        onChange={(e) => setShopDomain(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') conectarShopify(); }}
                        placeholder={t('stores.shopify_domain_placeholder')}
                        className="input"
                        autoFocus
                    />
                    <p className="mt-1 text-xs text-slate-500">{t('stores.shopify_domain_help')}</p>
                </ModalShell>
            )}
        </div>
    );
}
